import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faXTwitter,
  faInstagram,
  faLinkedin,
  faTiktok
} from '@fortawesome/free-brands-svg-icons';
import { FaBug } from 'react-icons/fa';
import BugReportModal from './BugReport/BugReportModal';
import './Footer.css';

const Footer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <footer className="footer">
      <div className="footer-content">
        {/* Main Footer Content */}
        <div className="footer-main">
          <div className="footer-grid">
            
            {/* Company Info */}
            <div className="footer-section">
              <div className="footer-logo">
                <h3 className="logoName">
                  Way<span className="colored-part">Share</span>
                </h3>
                <p className="footer-tagline">
                  {t('footer.tagline', 'Connecting journeys, delivering dreams')}
                </p>
              </div>
              <p className="footer-description">
                {t('footer.description', 'The smart way to share rides and send packages. Join thousands of users who trust WayShare for their daily commutes and delivery needs.')}
              </p>
              <div className="footer-contact-info">
                <div className="contact-item">
                  <span className="contact-icon">✉</span>
                  <span>info@wayshare.app</span>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="footer-section">
              <h4 className="footer-title">{t('footer.services', 'Services')}</h4>
              <ul className="footer-links">
                <li>
                  <button onClick={() => handleNavigation('/PublishPost')} className="footer-link">
                    <span className="service-icon">🚗</span>
                    {t('footer.ridesharing', 'Ridesharing')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/SendPackage')} className="footer-link">
                    <span className="service-icon">📦</span>
                    {t('footer.packageDelivery', 'Package Delivery')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div className="footer-section">
              <h4 className="footer-title">{t('footer.quickLinks', 'Quick Links')}</h4>
              <ul className="footer-links">
                <li>
                  <button onClick={() => handleNavigation('/home')} className="footer-link">
                    {t('footer.home', 'Home')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/About')} className="footer-link">
                    {t('footer.about', 'About Us')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/benefits')} className="footer-link">
                    {t('footer.benefits', 'Benefits')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/Contact')} className="footer-link">
                    {t('footer.contact', 'Contact')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/MyprofileForm')} className="footer-link">
                    {t('footer.myProfile', 'My Profile')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/Settings')} className="footer-link">
                    {t('footer.settings', 'Settings')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal & Support */}
            <div className="footer-section">
              <h4 className="footer-title">{t('footer.support', 'Support & Legal')}</h4>
              <ul className="footer-links">
                <li>
                  <button 
                    onClick={() => setIsBugReportOpen(true)} 
                    className="footer-link footer-bug-report-btn"
                  >
                    <FaBug className="footer-bug-icon" />
                    {t('footer.reportBug', 'Report Bug')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/safety')} className="footer-link">
                    {t('footer.safety', 'Safety Guidelines')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/terms')} className="footer-link">
                    {t('footer.terms', 'Terms of Service')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/privacy')} className="footer-link">
                    {t('footer.privacy', 'Privacy Policy')}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleNavigation('/cookies')} className="footer-link">
                    {t('footer.cookies', 'Cookie Policy')}
                  </button>
                </li>
              </ul>
            </div>

          </div>

        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            
            {/* Copyright */}
            <div className="footer-copyright">
              <p>
                © {currentYear} <span className="brand-name">WayShare</span>.
                {t('footer.copyright', 'All rights reserved.')}
              </p>
              <p className="footer-made-with">
                {t('footer.madeWith', 'Made with')} <span className="heart-icon">❤️</span> {t('footer.inLatvia', 'in Latvia')}
              </p>
            </div>

            {/* Social Media */}
            <div className="footer-social">
              <h5 className="social-title">{t('footer.followUs', 'Follow Us')}</h5>
              <div className="social-links">
                <a href="https://www.facebook.com/profile.php?id=61579729680575" aria-label="Facebook" className="social-link">
                  <FontAwesomeIcon icon={faFacebook} />
                </a>
                <a href="https://x.com/wayshareapp" aria-label="X (Twitter)" className="social-link">
                  <FontAwesomeIcon icon={faXTwitter} />
                </a>
                <a href="https://instagram.com/wayshareapp" aria-label="Instagram" className="social-link">
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
                <a href="https://tiktok.com/@wayshareapp" aria-label="TikTok" className="social-link">
                  <FontAwesomeIcon icon={faTiktok} />
                </a>
              </div>
            </div>

            {/* App Downloads 
            <div className="footer-apps">
              <h5 className="apps-title">{t('footer.downloadApp', 'Download Our App')}</h5>
              <div className="app-buttons">
                <a href="#" className="app-button apple-store">
                  <span className="app-button-text">
                    <small>{t('footer.downloadOn', 'Download on the')}</small>
                    <strong>App Store</strong>
                  </span>
                </a>
                <a href="#" className="app-button google-play">
                  <span className="app-button-text">
                    <small>{t('footer.getItOn', 'Get it on')}</small>
                    <strong>Google Play</strong>
                  </span>
                </a>
              </div>
            </div>*/}

          </div>
        </div>
      </div>
      
      {/* Bug Report Modal */}
      <BugReportModal 
        isOpen={isBugReportOpen} 
        onClose={() => setIsBugReportOpen(false)} 
      />
    </footer>
  );
};

export default Footer;